/**
 * Sidebar Component
 * Manages chat sessions, user profile, and navigation
 * Features: Grouped navigation (5 groups), session list, new session creation, user info display
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  Drawer,
  TextField,
  useTheme,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import {
  Add as AddIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreIcon,
  Schedule as TimeIcon,
  Info as InfoIcon,
  Assignment as EntitlementsIcon,
  Description as PaidIcon,
  Gavel as ClaimsIcon,
  LocalHospital as MentalHealthIcon,
  Balance as BalanceIcon,
  Dashboard as DashboardIcon,
  Warning as CrisisIcon,
  Event as EventIcon,
  Explore as ExploreIcon,
  FolderSpecial as VaultIcon,
  NotificationsNone as NotificationsIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandMoreIcon,
  MenuOpen as MenuOpenIcon,
  Groups as GroupsIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { signOut } from "aws-amplify/auth";
import apiService from "../services/apiService";
import { getMilitarySessionTitle } from "../utils/sessionUtils";
import simpleChatLogic from "../services/SimpleChatLogic";
import { loadAdvocateSessions } from "../utils/advocateSessions";
// Phase 999.100 W2 (D-08, Pattern A) — persistent veteran-initiated escalation
// button. Lives in the Support & Wellbeing group so it's discoverable next to
// the Crisis Resources entry without being conflated with it.
import AskSupportButton from "./support/AskSupportButton";

// ── Advocate session list — Phase 999.102: cloud-backed via /sessions API ─────
//
// Pre-999.102 this hook read sessions from localStorage. After 999.102 the
// shared `loadAdvocateSessions(userId)` helper hits `session_manager_v5` over
// the network, so we use a useEffect to fetch on mount + on the
// 'advocate-sessions-updated' event. Same shape returned to consumers.
function useAdvocateSessions(userId) {
  const [advocateSessions, setAdvocateSessions] = React.useState([]);

  React.useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const sessions = await loadAdvocateSessions(userId);
        if (!cancelled) setAdvocateSessions(sessions);
      } catch (_) {
        if (!cancelled) setAdvocateSessions([]);
      }
    };
    refresh();
    window.addEventListener('advocate-sessions-updated', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      cancelled = true;
      window.removeEventListener('advocate-sessions-updated', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [userId]);

  return advocateSessions;
}

/**
 * Persists sidebar section expanded/collapsed state in localStorage.
 * Key: afterservice_sidebar_sections_{userId}
 * Default: ai-advocate=true, all others=false
 */
function useSidebarSections(userId) {
  const STORAGE_KEY = userId ? `afterservice_sidebar_sections_${userId}` : null;
  const DEFAULT_STATE = {
    'my-service': false,
    'tools': false,
    'support': false,
    'community': false,
    'ai-advocate': true,
  };

  const [sections, setSections] = useState(() => {
    if (!STORAGE_KEY) return DEFAULT_STATE;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults so new sections added in future get a default
        return { ...DEFAULT_STATE, ...parsed };
      }
    } catch (_) {
      // ignore parse errors
    }
    return DEFAULT_STATE;
  });

  const toggleSection = useCallback((id) => {
    setSections((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      if (STORAGE_KEY) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch (_) {
          // quota exceeded — silent fail
        }
      }
      return next;
    });
  }, [STORAGE_KEY]);

  return { sections, toggleSection };
}

/**
 * SidebarNavGroup — collapsible accordion nav group with MUI Collapse animation.
 * Renders a labelled section of nav items with chevron toggle and localStorage persistence.
 */
function SidebarNavGroup({ label, id, children, expanded, onToggle }) {
  return (
    <Box component="section" aria-labelledby={`nav-group-${id}`} sx={{ mb: 1 }}>
      {/* Section header — clickable to toggle */}
      <Box
        component="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`nav-group-content-${id}`}
        id={`nav-group-${id}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          px: 2,
          pt: 2,
          pb: 0.5,
          borderRadius: 1,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
        }}
      >
        <Typography
          sx={{
            fontSize: '11.2px',
            fontWeight: 600,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            color: '#9ca3af',
          }}
        >
          {label}
        </Typography>
        <ExpandMoreIcon
          sx={{
            color: '#9ca3af',
            fontSize: 16,
            transition: 'transform 0.2s ease',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </Box>

      {/* Collapsible content */}
      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        <Box id={`nav-group-content-${id}`}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * SidebarSessionPreview — read-only compact session list for digital assistant section.
 * Shows last 3 sessions. No rename/delete actions.
 *
 * @param {{ sessions: Array, currentSession: object|null, onSessionSelect: function }} props
 */
function SidebarSessionPreview({ sessions, currentSession, onSessionSelect }) {
  const navigate = useNavigate();

  // sessions prop is already sorted newest-first from session_manager_v4
  const recentSessions = (sessions || []).slice(0, 3);

  if (recentSessions.length === 0) return null;

  const handleSessionClick = (session) => {
    navigate('/assistant');
    if (onSessionSelect) {
      onSessionSelect(session);
    }
  };

  return (
    <Box sx={{ mt: 0.5 }}>
      {/* Session items — max-height scrollable */}
      <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
        {recentSessions.map((session) => {
          const isActive = currentSession?.sessionId === session.sessionId;
          const title = getMilitarySessionTitle(session) || session.title || 'Conversation';

          return (
            <ListItemButton
              key={session.sessionId}
              onClick={() => handleSessionClick(session)}
              selected={isActive}
              sx={{
                pl: 3,
                pr: 2,
                py: 0.5,
                borderRadius: 1,
                mx: 1,
                mb: 0.25,
                '&.Mui-selected': {
                  bgcolor: 'rgba(80, 134, 237, 0.15)',
                  '&:hover': { bgcolor: 'rgba(80, 134, 237, 0.2)' },
                },
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.06)' },
              }}
            >
              <ListItemText
                primary={title}
                primaryTypographyProps={{
                  noWrap: true,
                  sx: {
                    fontSize: '12.5px',
                    color: isActive ? 'var(--as-blue-500)' : '#d1d5db',
                    fontWeight: isActive ? 500 : 400,
                  },
                }}
              />
            </ListItemButton>
          );
        })}
      </Box>

      {/* View all chats link */}
      <Box
        component="button"
        onClick={() => navigate('/assistant')}
        sx={{
          display: 'block',
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          px: 3,
          py: 0.75,
          color: 'var(--as-blue-500)',
          fontSize: '12px',
          fontWeight: 500,
          '&:hover': { textDecoration: 'underline' },
        }}
        aria-label="View all chat sessions"
      >
        View all chats →
      </Box>
    </Box>
  );
}

// Flat icon list for the collapsed 64px mini-rail (icon-only). Mirrors the
// grouped navigation rendered in drawerContent; `divider:true` entries separate
// the original groups. Reuses the same icon components as the expanded rail so
// the two stay in sync visually.
const RAIL_ITEMS = [
  { label: "Dashboard", path: "/", Icon: DashboardIcon },
  { label: "My Profile", path: "/profile", Icon: PersonIcon },
  { label: "Document Vault", path: "/documents", Icon: VaultIcon },
  { label: "Transition Timeline", path: "/transition", Icon: TimelineIcon },
  { label: "Notifications", path: "/notifications", Icon: NotificationsIcon },
  { divider: true },
  { label: "Entitlements", path: "/entitlements", Icon: PaidIcon },
  { label: "My Claims", path: "/claims", Icon: ClaimsIcon },
  { label: "Appeals", path: "/appeals", Icon: BalanceIcon },
  { divider: true },
  { label: "Mental Health Hub", path: "/mental-health", Icon: MentalHealthIcon, color: "error.main" },
  { label: "Crisis Resources", path: "/crisis", Icon: CrisisIcon, color: "error.main" },
  { divider: true },
  { label: "Events", path: "/events", Icon: EventIcon },
  { label: "Find Support", path: "/find-support", Icon: GroupsIcon },
  { label: "My Organisations", path: "/my-orgs", Icon: BusinessIcon },
  { divider: true },
  { label: "Talk to Your Assistant", path: "/assistant", Icon: ChatIcon },
];

const Sidebar = ({
  sessions,
  currentSession,
  onSessionSelect,
  onSessionCreate,
  onSessionUpdate,
  onSessionDelete,
  user,
  onRefreshSessions,
  mobileDrawerOpen,
  onMobileDrawerToggle,
  sidebarOpen,      // optional controlled open state from parent
  onSidebarToggle,  // optional controlled toggle handler from parent
}) => {
  // Collapsible sidebar sections — persisted in localStorage
  const { sections, toggleSection } = useSidebarSections(user?.userId || user?.sub);

  // Advocate sessions from localStorage (AgentCore — not DynamoDB)
  const advocateSessions = useAdvocateSessions(user?.userId || user?.sub || 'anonymous');

  // State for profile data
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Load profile data on component mount, and again whenever another surface
  // edits the profile (e.g. the "I am a" user-type dropdown on the profile
  // page). Without the 'profile-updated' listener the nav keeps gating on the
  // userType captured at mount, so a type change would not re-gate the nav
  // until a full reload.
  useEffect(() => {
    if (!user?.userId) return undefined;

    const loadProfileData = async () => {
      try {
        setProfileLoading(true);
        const response = await apiService.getUserProfile();
        setProfileData(response);
      } catch (error) {
        console.error("Failed to load profile data:", error);
      } finally {
        setProfileLoading(false);
      }
    };

    loadProfileData();
    window.addEventListener("profile-updated", loadProfileData);
    return () => window.removeEventListener("profile-updated", loadProfileData);
  }, [user?.userId]);

  // Helper function to format user display name
  const getUserDisplayName = () => {
    // Priority: Profile data first, then Cognito data, then fallback
    if (profileData?.firstName && profileData?.lastName) {
      return `${profileData.firstName} ${profileData.lastName}`;
    } else if (profileData?.firstName) {
      return profileData.firstName;
    } else if (user?.given_name && user?.family_name) {
      return `${user.given_name} ${user.family_name}`;
    } else if (user?.given_name) {
      return user.given_name;
    } else if (user?.family_name) {
      return user.family_name;
    } else {
      return user?.email || "Veteran";
    }
  };

  // Profile meta line for the bottom profile bar — the user type as a full word
  // (e.g. "Veteran"). Known types get a curated label; any future user type
  // falls back to a capitalised form of its raw value, so new types render
  // correctly without a code change here.
  const USER_TYPE_LABELS = {
    veteran: "Veteran",
    spouse: "Spouse",
    partner: "Partner",
    family: "Family",
    "family-member": "Family Member",
    supporter: "Supporter",
    eso: "VSO",
    organisation: "Organisation",
  };
  const getServiceMeta = () => {
    // Pipe-delimited multi-type — show the primary (first) type.
    const raw = (profileData?.userType || "veteran").toLowerCase().split("|")[0];
    return USER_TYPE_LABELS[raw] || (raw.charAt(0).toUpperCase() + raw.slice(1));
  };

  // User-types expansion decision #4/#5 — nav gating per type:
  // veteran-only: Transition Timeline, Entitlements, My Claims, Appeals.
  // veteran+family: Document Vault, Notifications, My Organisations.
  // everyone: Dashboard, Profile, Mental Health, Crisis, Events,
  // Find Support, Advocate chat.
  const sidebarTypes = (profileData?.userType || "veteran")
    .toLowerCase()
    .split("|")
    .filter(Boolean);
  const navIsVeteran = sidebarTypes.includes("veteran");
  const navIsFamily =
    sidebarTypes.includes("spouse") || sidebarTypes.includes("family-member");

  // Handle navigation to profile
  const handleProfileNavigation = () => {
    navigate("/profile");
  };

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSessionForMenu, setSelectedSessionForMenu] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newSessionTitle, setNewSessionTitle] = useState("");
  const [dialogSession, setDialogSession] = useState(null); // Persistent session for dialogs
  const [userProfileOpen, setUserProfileOpen] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [error, setError] = useState(null);
  const [desktopOpen, setDesktopOpen] = useState(true); // Desktop drawer state (uncontrolled fallback)
  // If parent passes sidebarOpen, use it; otherwise use internal state
  const effectiveDesktopOpen = sidebarOpen !== undefined ? sidebarOpen : desktopOpen;

  // Toggle drawer based on screen size
  const handleDrawerToggle = () => {
    if (isMobile) {
      onMobileDrawerToggle();
    } else if (onSidebarToggle) {
      onSidebarToggle(); // controlled by parent (AppLayout)
    } else {
      setDesktopOpen(!desktopOpen); // uncontrolled fallback
    }
  };

  // Create new session (force new) with automatic Lambda warmup
  const handleCreateSession = async () => {
    setIsCreatingSession(true);
    setError(null);

    try {
      // Step 1: Create the session in DynamoDB first
      const response = await apiService.createChatSession(true); // true = force new session

      // Backend returns session directly, not wrapped in response.session
      const session = response.session || response;

      // Step 2: Set new session as current (ChatInterface will handle WebSocket connection)
      onSessionCreate(session);
      onSessionSelect(session);
    } catch (error) {
      console.error("Failed to create session:", error);
      setError("Failed to create new session. Please try again.");
    } finally {
      setIsCreatingSession(false);
    }
  };

  // Handle session menu
  const handleSessionMenu = (event, session) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedSessionForMenu(session);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedSessionForMenu(null);
  };

  // Delete session
  const handleDeleteSession = async () => {
    if (!dialogSession) {
      console.error("No session selected for deletion");
      return;
    }

    try {
      // Phase 999.102 — single source. Both advocate and chat-handler sessions
      // live in the same DDB table behind /sessions. One delete call covers
      // every session type.
      const sessionId = dialogSession.sessionId;
      await apiService.deleteSession(sessionId);

      onSessionDelete(sessionId);

      // Phase 999.102 — Sidebar's advocate-session list listens for this event;
      // chat sessions are refreshed via onSessionDelete -> parent state.
      window.dispatchEvent(new Event('advocate-sessions-updated'));

      // If deleted session was current, clear selection
      if (currentSession?.sessionId === sessionId) {
        onSessionSelect(null);
      }
    } catch (error) {
      console.error("Failed to delete session:", error);
      setError("Failed to delete session. Please try again.");
    } finally {
      setDeleteDialogOpen(false);
      setDialogSession(null); // Clear dialog session
    }
  };

  // Rename session
  // PROD review 2026-07-30 #6 — the rename affordance writes through the same
  // save path the advocate chat uses (createOrUpdateSession with
  // source='advocate'), then pokes the advocate list to refresh.
  const handleRenameSession = async () => {
    if (!dialogSession || !newSessionTitle.trim()) {
      console.error("No session selected or empty title for rename");
      return;
    }

    try {
      const title = newSessionTitle.trim();
      await apiService.createOrUpdateSession({
        sessionId: dialogSession.sessionId,
        title,
        source: 'advocate',
      });

      // Sidebar's advocate-session list listens for this event.
      window.dispatchEvent(new Event('advocate-sessions-updated'));

      // Update the session in the parent component (chat-session lists)
      if (typeof onSessionUpdate === 'function') {
        onSessionUpdate({ ...dialogSession, title });
      }
    } catch (error) {
      console.error("Failed to rename session:", error);
      setError("Failed to rename conversation. Please try again.");
    } finally {
      setRenameDialogOpen(false);
      setNewSessionTitle("");
      setDialogSession(null); // Clear dialog session
    }
  };

  // Open rename dialog
  const handleOpenRenameDialog = () => {
    if (selectedSessionForMenu) {
      setDialogSession(selectedSessionForMenu); // Store session for dialog
      // Advocate list rows carry a display title (already neutralised on
      // load). The previous getSessionTitle() helper returned an object,
      // which put "[object Object]" in the rename field.
      setNewSessionTitle(selectedSessionForMenu.title || 'Conversation');
      setRenameDialogOpen(true);
    }
    handleCloseMenu();
  };

  // Handle logout with proper WebSocket cleanup
  const handleLogout = async () => {
    try {
      simpleChatLogic.disconnect(true);
      await signOut();
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      window.location.href = '/';
    }
  };

  // Format date for session display (day only, since time is in chat title)
  const formatSessionDate = (timestamp) => {
    try {
      // Handle different timestamp formats
      let date;

      if (!timestamp) {
        // If no timestamp, return a default
        return "Unknown";
      }

      // If it's already a Date object
      if (timestamp instanceof Date) {
        date = timestamp;
      }
      // If it's a number (Unix timestamp)
      else if (typeof timestamp === "number") {
        // Check if it's in seconds or milliseconds
        if (timestamp < 10000000000) {
          // Likely seconds, convert to milliseconds
          date = new Date(timestamp * 1000);
        } else {
          // Already in milliseconds
          date = new Date(timestamp);
        }
      }
      // If it's a string (ISO format or other)
      else if (typeof timestamp === "string") {
        date = new Date(timestamp);
      }
      // Fallback
      else {
        date = new Date(timestamp);
      }

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      // Always show day-based format since time is now in the chat title
      if (diffDays === 0) {
        return "Today";
      } else if (diffDays === 1) {
        return "Yesterday";
      } else if (diffDays < 7) {
        return new Intl.DateTimeFormat("en-AU", {
          weekday: "short",
          timeZone: "Australia/Sydney",
        }).format(date);
      } else {
        return new Intl.DateTimeFormat("en-AU", {
          month: "short",
          day: "numeric",
          timeZone: "Australia/Sydney",
        }).format(date);
      }
    } catch (error) {
      console.error("Error parsing date:", error, "Timestamp:", timestamp);
      return "Error";
    }
  };

  /**
   * NavItem — renders a single nav link with active state detection.
   * Active detection uses location.pathname from useLocation().
   * Disabled items (e.g. Events Coming Soon) are non-interactive per WCAG.
   */
  const NavItem = ({ label, icon, path, color, disabled, chip }) => {
    const isActive = !disabled && location.pathname === path;
    return (
      <ListItemButton
        onClick={() => !disabled && navigate(path)}
        aria-current={isActive ? 'page' : undefined}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
        sx={{
          borderRadius: '0 8px 8px 0',
          py: 0.8,
          px: 2,
          mb: 0.25,
          minHeight: 44, // WCAG 2.5.5 touch target
          // v1.1: active = brand-wash well + brand left-rule + brand-dark text.
          color: isActive ? 'var(--as-brand-dark)' : (color || 'text.primary'),
          bgcolor: isActive ? 'var(--as-brand-wash)' : 'transparent',
          borderLeft: isActive ? '4px solid var(--as-brand)' : '4px solid transparent',
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : undefined,
          '& .MuiListItemIcon-root': {
            minWidth: 30,
            color: isActive ? 'var(--as-brand)' : (color || 'var(--as-text-muted)'),
          },
          '&:hover': {
            bgcolor: disabled ? 'transparent' : (isActive ? 'var(--as-brand-wash)' : 'var(--as-paper-2)'),
          },
          transition: 'all 0.15s ease',
        }}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isActive ? 600 : 500 }}
        />
        {chip}
      </ListItemButton>
    );
  };

  // Sidebar content component
  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      {/* Header — the collapse/close control lives here (moved off the command
          bar per the design). Right-aligned, subtle; the user profile is pinned
          bottom-left and the brand logo lives in the top command bar. */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          minHeight: 44,
        }}
      >
        <Tooltip
          title={
            isMobile
              ? "Close menu"
              : effectiveDesktopOpen
                ? "Collapse menu"
                : "Expand menu"
          }
        >
          <IconButton
            onClick={handleDrawerToggle}
            size="small"
            aria-label={isMobile ? "Close menu" : "Collapse menu"}
            sx={{
              color: "var(--as-text-muted)",
              "&:hover": { bgcolor: "var(--as-paper-2)", color: "var(--as-text)" },
            }}
          >
            <MenuOpenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 1 }}>
          {error}
        </Alert>
      )}

      {/* Main Navigation — 5 groups, flat (no accordion) */}
      <Box
        component="nav"
        aria-label="Main navigation"
        sx={{ overflowY: 'auto', flexShrink: 0 }}
      >
        <SidebarNavGroup label="My Service" id="my-service" expanded={sections['my-service']} onToggle={() => toggleSection('my-service')}>
          <NavItem
            label="Dashboard"
            icon={<DashboardIcon sx={{ fontSize: 18 }} />}
            path="/"
          />
          <NavItem
            label="My Profile"
            icon={<PersonIcon sx={{ fontSize: 18 }} />}
            path="/profile"
          />
          {(navIsVeteran || navIsFamily) && (
            <NavItem
              label="Document Vault"
              icon={<VaultIcon sx={{ fontSize: 18 }} />}
              path="/documents"
            />
          )}
          {navIsVeteran && (
            <NavItem
              label="Transition Timeline"
              icon={<TimelineIcon sx={{ fontSize: 18 }} />}
              path="/transition"
            />
          )}
          {(navIsVeteran || navIsFamily) && (
            <NavItem
              label="Notifications"
              icon={<NotificationsIcon sx={{ fontSize: 18 }} />}
              path="/notifications"
            />
          )}
        </SidebarNavGroup>

        {navIsVeteran && (
        <SidebarNavGroup label="Tools" id="tools" expanded={sections['tools']} onToggle={() => toggleSection('tools')}>
          <NavItem
            label="Entitlements"
            icon={<PaidIcon sx={{ fontSize: 18 }} />}
            path="/entitlements"
          />
          {/* Health Cards removed 2026-04-28 — already accessible as the
              "Health cards" tab inside My Claims (/claims?tab=4); the
              top-level nav entry was redundant. */}
          <NavItem
            label="My Claims"
            icon={<ClaimsIcon sx={{ fontSize: 18 }} />}
            path="/claims"
          />
          <NavItem
            label="Appeals"
            icon={<BalanceIcon sx={{ fontSize: 18 }} />}
            path="/appeals"
          />
        </SidebarNavGroup>
        )}

        <SidebarNavGroup label="Support &amp; Wellbeing" id="support" expanded={sections['support']} onToggle={() => toggleSection('support')}>
          <NavItem
            label="Mental Health Hub"
            icon={<MentalHealthIcon sx={{ fontSize: 18 }} />}
            path="/mental-health"
            color="error.main"
          />
          <NavItem
            label="Crisis Resources"
            icon={<CrisisIcon sx={{ fontSize: 18 }} />}
            path="/crisis"
            color="error.main"
          />
          {/* DORMANT 2026-05-17 — "Ask support to reach out" hidden pending T&Cs sign-off.
              Re-enable by un-commenting the Box below. The AskSupportButton component +
              AskSupportConfirmModal + /support/request-contact Lambda all remain intact. */}
          {/*
          <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
            <AskSupportButton surface="sidebar" sx={{ width: '100%', justifyContent: 'flex-start' }} />
          </Box>
          */}
        </SidebarNavGroup>

        <SidebarNavGroup label="Community" id="community" expanded={sections['community']} onToggle={() => toggleSection('community')}>
          <NavItem
            label="Events"
            icon={<EventIcon sx={{ fontSize: 18 }} />}
            path="/events"
          />
          <NavItem
            label="Find Support"
            icon={<GroupsIcon sx={{ fontSize: 18 }} />}
            path="/find-support"
          />
          {(navIsVeteran || navIsFamily) && (
            <NavItem
              label="My Organisations"
              icon={<BusinessIcon sx={{ fontSize: 18 }} />}
              path="/my-orgs"
            />
          )}
        </SidebarNavGroup>

        <SidebarNavGroup label="Digital assistant" id="ai-advocate" expanded={sections['ai-advocate']} onToggle={() => toggleSection('ai-advocate')}>
          <NavItem
            label="Talk to Your Assistant"
            icon={<ChatIcon sx={{ fontSize: 18 }} />}
            path="/assistant"
          />

          {/* New conversation button */}
          <Box sx={{ px: 2, pb: 1 }}>
            <Button
              fullWidth
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/assistant?new=1')}
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8rem',
                borderColor: 'rgba(80,134,237,0.4)',
                color: 'var(--as-blue-500)',
                '&:hover': { borderColor: 'var(--as-blue-500)', bgcolor: 'rgba(80,134,237,0.08)' },
              }}
            >
              New conversation
            </Button>
          </Box>

          {/* Advocate Sessions List (AgentCore — localStorage) */}
          <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
            <List sx={{ p: 0 }}>
              {advocateSessions.length === 0 ? (
                <Box sx={{ px: 2, py: 2, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    No conversations yet. Open the Assistant to start one.
                  </Typography>
                </Box>
              ) : (
                advocateSessions
                  .filter((session) => session && session.sessionId)
              .map((session) => (
                <ListItem
                  key={session.sessionId}
                  disablePadding
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      className="as-conv-menu"
                      aria-label={`Options for conversation ${session.title || 'Conversation'}`}
                      onClick={(e) => { e.stopPropagation(); handleSessionMenu(e, session); }}
                      sx={{
                        color: "var(--as-text-muted)",
                        opacity: 0,
                        transition: "opacity 0.12s ease",
                        ".MuiListItem-root:hover &": { opacity: 1 },
                      }}
                    >
                      <MoreIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  }
                  sx={{
                    "& .MuiListItemSecondaryAction-root": { right: 4 },
                  }}
                >
                  {/* v1.1: compact single-line conversation row — title only, like
                      Claude/ChatGPT. Date/count removed to reclaim rail space. */}
                  <ListItemButton
                    onClick={() => navigate(`/assistant?session=${encodeURIComponent(session.sessionId)}`)}
                    sx={{
                      px: 2,
                      py: 0.5,
                      minHeight: 32,
                      borderRadius: 0,
                      pr: 4,
                    }}
                  >
                    <ListItemText
                      primary={session.title || 'Conversation'}
                      primaryTypographyProps={{
                        noWrap: true,
                        sx: { fontSize: "0.8rem", color: "var(--as-text)" },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))
              )}
            </List>
          </Box>

        </SidebarNavGroup>
      </Box>

      {/* ── User profile — pinned bottom-left (v1.1 / Claude·ChatGPT pattern) ──
          mt:auto pushes this below the flex:1 nav scroll area. Avatar shows the
          user's real initials (not the brand logo). Reuses existing handlers —
          no new state or behaviour. */}
      <Box
        sx={{
          mt: "auto",
          borderTop: "1px solid var(--as-border-soft)",
          p: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box
          onClick={handleProfileNavigation}
          role="button"
          tabIndex={0}
          aria-label="View your profile"
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleProfileNavigation(); } }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: 1,
            minWidth: 0,
            p: 0.75,
            borderRadius: 2,
            cursor: "pointer",
            transition: "background 0.15s ease",
            "&:hover": { bgcolor: "var(--as-brand-wash)" },
            "&:focus-visible": { outline: "2px solid var(--as-brand)", outlineOffset: 2 },
          }}
        >
          <Avatar
            sx={{
              bgcolor: "var(--as-brand)",
              color: "#fff",
              width: 36,
              height: 36,
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            {getUserDisplayName()
              .split(" ")
              .map((n) => n[0])
              .filter(Boolean)
              .slice(0, 2)
              .join("")
              .toUpperCase() || "V"}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              noWrap
              sx={{ fontWeight: 600, fontSize: "0.85rem", lineHeight: 1.2 }}
            >
              {getUserDisplayName()}
            </Typography>
            <Typography
              noWrap
              sx={{
                fontFamily: "var(--as-font-mono)",
                fontSize: "0.66rem",
                letterSpacing: "0.04em",
                color: "var(--as-text-muted)",
                lineHeight: 1.3,
              }}
            >
              {getServiceMeta()}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Sign Out">
          <IconButton
            onClick={(e) => { e.stopPropagation(); handleLogout(); }}
            size="small"
            aria-label="Sign out"
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Session Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {/* #31 — actions carry the conversation title in their accessible
            names so screen-reader users know which conversation they act on. */}
        <MenuItem
          onClick={handleOpenRenameDialog}
          aria-label={`Rename conversation ${selectedSessionForMenu?.title || 'Conversation'}`}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedSessionForMenu) {
              setDialogSession(selectedSessionForMenu); // Store session for dialog
              setDeleteDialogOpen(true);
            }
            handleCloseMenu();
          }}
          aria-label={`Delete conversation ${selectedSessionForMenu?.title || 'Conversation'}`}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDialogSession(null);
        }}
        maxWidth="sm"
        fullWidth
        disablePortal
      >
        <DialogTitle>Delete Chat Session</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this chat session? This action
            cannot be undone and will permanently remove all conversation
            history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDialogSession(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSession}
            color="error"
            variant="contained"
            aria-label={`Delete conversation ${dialogSession?.title || 'Conversation'}`}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Session Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => {
          setRenameDialogOpen(false);
          setNewSessionTitle("");
          setDialogSession(null);
        }}
        maxWidth="sm"
        fullWidth
        disablePortal
      >
        <DialogTitle>Rename Chat Session</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Session Title"
            type="text"
            fullWidth
            variant="outlined"
            value={newSessionTitle}
            onChange={(e) => setNewSessionTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleRenameSession();
              }
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRenameDialogOpen(false);
              setNewSessionTitle("");
              setDialogSession(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRenameSession}
            variant="contained"
            disabled={!newSessionTitle.trim()}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Profile Dialog */}
      <Dialog
        open={userProfileOpen}
        onClose={() => setUserProfileOpen(false)}
        maxWidth="sm"
        fullWidth
        disablePortal
      >
        <DialogTitle>User Profile</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "primary.main", width: 64, height: 64 }}>
                <PersonIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                {/* Phase 999.106 W1: profile-widget display name is a label,
                    not document structure. Demote from default <h6> to <span>
                    so screen-reader heading rotor isn't polluted by the
                    sidebar's per-user widget. */}
                <Typography variant="h6" component="span">{getUserDisplayName()}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.userId || "Unknown ID"}
                </Typography>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Veteran Support Information
              </Typography>
              <Chip
                icon={<InfoIcon />}
                label="Veteran Focused Platform"
                color="primary"
                sx={{ mb: 1, mr: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Supporting your journey with secure, personalized assistance for
                DVA claims, healthcare, and benefits.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserProfileOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // ── Collapsed mini-rail (desktop, 64px) ─────────────────────────────────
  // Icon-only navigation with hover tooltips and an always-visible expand
  // control at the top — best-practice collapse (no fully-hidden rail, no
  // floating button). Active route is highlighted with the brand wash.
  const collapsedRail = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        bgcolor: "background.paper",
      }}
    >
      {/* Expand control — mirrors the header toggle, rotated to point right. */}
      <Box sx={{ py: 1, display: "flex", justifyContent: "center", width: "100%" }}>
        <Tooltip title="Expand menu" placement="right">
          <IconButton
            onClick={handleDrawerToggle}
            size="small"
            aria-label="Expand menu"
            aria-expanded={false}
            sx={{
              color: "var(--as-text-muted)",
              "&:hover": { bgcolor: "var(--as-paper-2)", color: "var(--as-text)" },
            }}
          >
            <MenuOpenIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Icon nav — scrollable if it overflows. */}
      <Box
        component="nav"
        aria-label="Main navigation"
        sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", width: "100%", py: 0.5 }}
      >
        {RAIL_ITEMS.map((item, i) =>
          item.divider ? (
            <Divider key={`rail-divider-${i}`} sx={{ my: 0.5, mx: 1.5, borderColor: "var(--as-border-soft)" }} />
          ) : (
            <Box key={item.path} sx={{ display: "flex", justifyContent: "center", mb: 0.25 }}>
              <Tooltip title={item.label} placement="right">
                <IconButton
                  onClick={() => navigate(item.path)}
                  aria-label={item.label}
                  aria-current={location.pathname === item.path ? "page" : undefined}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    color: location.pathname === item.path ? "var(--as-brand)" : (item.color || "var(--as-text-muted)"),
                    bgcolor: location.pathname === item.path ? "var(--as-brand-wash)" : "transparent",
                    "&:hover": {
                      bgcolor: location.pathname === item.path ? "var(--as-brand-wash)" : "var(--as-paper-2)",
                    },
                    transition: "all 0.15s ease",
                  }}
                >
                  <item.Icon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )
        )}
      </Box>

      {/* Bottom — avatar (initials) + sign out, both with tooltips. */}
      <Box
        sx={{
          borderTop: "1px solid var(--as-border-soft)",
          py: 1,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Tooltip title={getUserDisplayName()} placement="right">
          <Box
            onClick={handleProfileNavigation}
            role="button"
            tabIndex={0}
            aria-label="View your profile"
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleProfileNavigation(); } }}
            sx={{
              cursor: "pointer",
              borderRadius: "50%",
              "&:focus-visible": { outline: "2px solid var(--as-brand)", outlineOffset: 2 },
            }}
          >
            <Avatar
              sx={{
                bgcolor: "var(--as-brand)",
                color: "#fff",
                width: 36,
                height: 36,
                fontSize: "0.85rem",
                fontWeight: 600,
              }}
            >
              {getUserDisplayName()
                .split(" ")
                .map((n) => n[0])
                .filter(Boolean)
                .slice(0, 2)
                .join("")
                .toUpperCase() || "V"}
            </Avatar>
          </Box>
        </Tooltip>
        <Tooltip title="Sign Out" placement="right">
          <IconButton onClick={handleLogout} size="small" aria-label="Sign out">
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  const drawerWidth = 300;

  return (
    <>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            borderRight: 1,
            borderColor: "divider",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer — its fixed paper is offset BELOW the 64px command bar.
          v1.1: always open; collapsing shrinks the paper to a 64px icon-only
          mini-rail rather than hiding it (best-practice — no floating button). */}
      <Drawer
        variant="persistent"
        open
        sx={{
          display: { xs: "none", sm: "block" },
          width: effectiveDesktopOpen ? drawerWidth : 64,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: effectiveDesktopOpen ? drawerWidth : 64,
            boxSizing: "border-box",
            top: "64px",
            height: "calc(100% - 64px)",
            overflowX: "hidden",
            borderRight: 1,
            borderColor: "divider",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {effectiveDesktopOpen ? drawerContent : collapsedRail}
      </Drawer>
    </>
  );
};

export default Sidebar;
